// LightLine shader: Creates rotating area light lines with realistic lighting calculations
// Inspired by rotating area lights concept with line segment distance functions
const frag = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_seed;
uniform float u_speed;

uniform float u_scale;          // Overall scale multiplier
uniform float u_lineLength;     // Length of light lines
uniform float u_numLines;       // Number of rotating lines
uniform float u_diffusePower;   // Diffuse lighting power
uniform float u_shinePower;     // Shine/intensity power
uniform float u_displacement;   // Line displacement amount
uniform float u_rotationSpeed;  // Rotation speed multiplier
uniform float u_intensity;      // Overall light intensity

uniform vec3 u_color_0;         // Primary light color
uniform vec3 u_color_1;         // Secondary light color  
uniform vec3 u_color_2;         // Accent color
uniform vec3 u_color_3;         // Background color

varying vec2 vUv;

// Line segment distance function
float line_segment(in vec2 p, in vec2 a, in vec2 b) {
    vec2 ba = b - a;
    vec2 pa = p - a;
    float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
    return length(pa - h * ba);
}

// Area light intensity calculation with side detection
vec2 area_light_intensity(vec2 p, vec2 v1, vec2 v2, float diffusePower, float shinePower) {  
    vec2 areaLine = normalize(v2 - v1);
    float l1 = dot(normalize(p - v1), areaLine);
    float l2 = dot(normalize(p - v2), areaLine);
    
    // Cross product to determine which side of the line the point is on
    vec3 cross_product = cross(vec3(p - v1, 0.0), vec3(areaLine, 0.0));
    
    float d = line_segment(p, v1, v2);
    float hh = max(0.0, pow(max(0.01, shinePower - d), 4.5));
    float c = (-l2 + l1) * hh;
    
    return vec2(pow(c, diffusePower), cross_product.z);
}

void main() {
    vec2 uv = (gl_FragCoord.xy - 0.5 * u_resolution.xy) / u_resolution.y;
    uv *= u_scale;
    
    float t = 0.0;
    vec3 col = vec3(0.0);
    
    int numLines = int(clamp(u_numLines, 1.0, 8.0));
    
    for (int i = 0; i < 8; i++) {
        if (i >= numLines) break;
        
        t += 1.0;
        float angle = (u_time * u_speed * u_rotationSpeed) / 10.0 + t * 5.0 + u_seed * 0.1;

        vec2 line = vec2(sin(angle), cos(angle));
        float s = u_lineLength;    
        vec2 v1 = -line * s;
        vec2 v2 = line * s;  
        
        vec2 disp = vec2(sin(t * 2.1 + u_seed), cos(t * 2.1 + u_seed)) * u_displacement;
        v1 += disp;
        v2 += disp;
        
        vec2 intensity_with_sign = area_light_intensity(uv, v1, v2, u_diffusePower, u_shinePower);
        float intensity = intensity_with_sign.x / pow(2.0, 7.0) * u_intensity;
        
        // Color based on which side of the line we're on
        vec3 lineColor;
        if (intensity_with_sign.y > 0.0) {
            // Positive side - brighter color
            lineColor = mix(u_color_0, u_color_1, t / float(numLines)) * intensity;
        } else {
            // Negative side - darker accent color
            lineColor = mix(u_color_2, u_color_3, t / float(numLines)) * intensity / 5.0;
        }
        
        col += lineColor;
    }

    // Tone mapping
    col = col / (1.0 + col);
    
    // Gamma correction
    col = sqrt(col);
    
    gl_FragColor = vec4(col, 1.0);
}
`;

const shader = {
  name: 'LightLine',
  shader: { frag },
  schema: [
    {
      key: 'colors',
      type: 'color-palette',
      label: 'Colors',
      min: 4,
      max: 4,
      default: ['#ff4040', '#ff6020', '#ff8010', '#1a0808'],
    },
    {
      key: 'u_scale',
      type: 'number',
      label: 'Scale',
      min: 1,
      max: 20,
      step: 0.1,
      default: 6.0,
    },
    {
      key: 'u_lineLength',
      type: 'number',
      label: 'Line Length',
      min: 0.5,
      max: 5,
      step: 0.1,
      default: 1.6,
    },
    {
      key: 'u_numLines',
      type: 'number',
      label: 'Number of Lines',
      min: 1,
      max: 8,
      step: 1,
      default: 3,
    },
    {
      key: 'u_diffusePower',
      type: 'number',
      label: 'Diffuse Power',
      min: 0.1,
      max: 5,
      step: 0.1,
      default: 1.0,
    },
    {
      key: 'u_shinePower',
      type: 'number',
      label: 'Shine Power',
      min: 1,
      max: 20,
      step: 0.5,
      default: 5.0,
    },
    {
      key: 'u_displacement',
      type: 'number',
      label: 'Line Displacement',
      min: 0,
      max: 5,
      step: 0.1,
      default: 2.0,
    },
    {
      key: 'u_rotationSpeed',
      type: 'number',
      label: 'Rotation Speed',
      min: 0.1,
      max: 5,
      step: 0.1,
      default: 1.0,
    },
    {
      key: 'u_intensity',
      type: 'number',
      label: 'Light Intensity',
      min: 0.1,
      max: 10,
      step: 0.1,
      default: 1.0,
    },
    { key: 'u_speed', type: 'number', label: 'Speed', min: 0, max: 3, step: 0.1, default: 1.0 },
    {
      key: 'u_seed',
      type: 'number',
      label: 'Random Seed',
      min: 0,
      max: 999,
      step: 1,
      default: 0,
    },
    { key: 'animated', type: 'boolean', label: 'Animated', default: true },
  ],
  examples: [
    'dynamic-shader(LightLine, colors=#ff4040,#ff6020,#ff8010,#1a0808; u_scale=6; u_numLines=3; u_shinePower=5;animated=true)',
    'dynamic-shader(LightLine, colors=#00ffff,#0080ff,#004080,#001020; u_scale=8; u_numLines=5; u_displacement=3; u_rotationSpeed=0.5; animated=true)',
    'dynamic-shader(LightLine, colors=#ff4040,#ff6020,#ff8010,#1a0808; u_scale=6; u_lineLength=2.5; u_numLines=3; u_diffusePower=1.2; u_shinePower=2.5; u_displacement=2.1; u_rotationSpeed=1; u_intensity=8.1; u_speed=1; u_seed=0; animated=false)',
  ],
};

export default shader;
